import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getCurrentShop, getCurrentUser } from "@/lib/auth";
import { getAvailableDestinations, createTransfer, sendTransfer, apiRequest } from "@/lib/api";
import { Loader2, Plus, Search, Trash2, Pencil, Send, Save } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface LineItem {
  inventoryId: string;
  productId: string;
  productName: string;
  quantity: number;
  unitCost: string;
  batchNumber: string;
  currentStock: number;
}

export function NewTransferDialog({
  show, onClose, onSuccess,
}: {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const currentShop = getCurrentShop()!;
  const currentUser = getCurrentUser();

  const [step, setStep] = useState(1);
  const [destinations, setDestinations] = useState<any[]>([]);
  const [shopDestinationId, setShopDestinationId] = useState("");
  const [notes, setNotes] = useState("");

  const [lines, setLines] = useState<LineItem[]>([]);
  const [productSearch, setProductSearch] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showSearch, setShowSearch] = useState(false);

  const [editingProduct, setEditingProduct] = useState<LineItem | null>(null);
  const [showAddProduct, setShowAddProduct] = useState(false);

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (show) {
      setStep(1);
      setShopDestinationId("");
      setNotes("");
      setLines([]);
      setProductSearch("");
      setSearchResults([]);
      setShowSearch(false);
      setEditingProduct(null);
      setShowAddProduct(false);
      getAvailableDestinations(currentShop.id).then(setDestinations).catch(() => setDestinations([]));
    }
  }, [show, currentShop.id]);

  const searchProducts = async (q: string) => {
    setProductSearch(q);
    if (q.length < 1) { setSearchResults([]); return; }
    try {
      const data = await apiRequest<any>(`/api/shops/${currentShop.id}/inventory?search=${encodeURIComponent(q)}&limit=10`);
      setSearchResults(Array.isArray(data) ? data : data?.data || []);
    } catch {
      setSearchResults([]);
    }
  };

  const selectProduct = (item: any) => {
    const exists = lines.find((l) => l.inventoryId === item.id);
    if (exists) {
      toast({ title: "Producto ya agregado", variant: "destructive" });
      return;
    }
    setEditingProduct({
      inventoryId: item.id,
      productId: item.productId,
      productName: item.product?.name || item.productName || "Producto",
      quantity: 1,
      unitCost: item.averageCost || item.acquisitionCost || "0",
      batchNumber: item.batchNumber || "",
      currentStock: item.stock || 0,
    });
    setShowAddProduct(true);
    setShowSearch(false);
    setProductSearch("");
    setSearchResults([]);
  };

  const confirmAddProduct = () => {
    if (!editingProduct) return;
    if (editingProduct.quantity <= 0) {
      toast({ title: "La cantidad debe ser mayor a 0", variant: "destructive" });
      return;
    }
    if (editingProduct.quantity > editingProduct.currentStock) {
      toast({ title: `Stock insuficiente. Disponible: ${editingProduct.currentStock}`, variant: "destructive" });
      return;
    }
    setLines((prev) => {
      const existing = prev.findIndex((l) => l.inventoryId === editingProduct.inventoryId);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = { ...editingProduct };
        return updated;
      }
      return [...prev, { ...editingProduct }];
    });
    setEditingProduct(null);
    setShowAddProduct(false);
  };

  const removeLine = (inventoryId: string) => {
    setLines((prev) => prev.filter((l) => l.inventoryId !== inventoryId));
  };

  const startEditLine = (inventoryId: string) => {
    const line = lines.find((l) => l.inventoryId === inventoryId);
    if (!line) return;
    setEditingProduct({ ...line });
    setShowAddProduct(true);
  };

  const handleSave = async (asDraft: boolean) => {
    if (!shopDestinationId) { toast({ title: "Selecciona una tienda destino", variant: "destructive" }); return; }
    if (lines.length === 0) { toast({ title: "Agrega al menos un producto", variant: "destructive" }); return; }

    setIsSaving(true);
    try {
      const transfer = await createTransfer(currentShop.id, {
        shopDestinationId,
        notes,
        items: lines.map((l) => ({
          inventoryId: l.inventoryId,
          productId: l.productId,
          quantity: l.quantity,
          unitCost: l.unitCost,
          batchNumber: l.batchNumber || undefined,
        })),
      });

      if (!asDraft) {
        await sendTransfer(currentShop.id, transfer.id);
      }

      toast({ title: asDraft ? "Borrador guardado" : "Transferencia enviada exitosamente" });
      onSuccess();
      onClose();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  const totalQuantity = lines.reduce((s, l) => s + l.quantity, 0);

  return (
    <Dialog open={show} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto p-4 sm:p-6" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>
            {step === 1
              ? "📤 Nueva Transferencia — Destino"
              : `📤 Nueva Transferencia — Productos (${lines.length})`}
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4 py-4">
            <div>
              <Label>Tienda destino *</Label>
              <Select value={shopDestinationId} onValueChange={setShopDestinationId}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar tienda..." />
                </SelectTrigger>
                <SelectContent>
                  {destinations.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notas</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notas opcionales..." />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 py-4">
            {/* Product list */}
            <div className="hidden md:block border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="p-2 text-left">#</th>
                    <th className="p-2 text-left">Producto</th>
                    <th className="p-2 text-right">Stock</th>
                    <th className="p-2 text-right">Cant a enviar</th>
                    <th className="p-2 text-right">Costo Und</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, i) => (
                    <tr key={line.inventoryId} className="border-b">
                      <td className="p-2">{i + 1}</td>
                      <td className="p-2 font-medium">{line.productName}</td>
                      <td className="p-2 text-right">{line.currentStock}</td>
                      <td className="p-2 text-right">{line.quantity}</td>
                      <td className="p-2 text-right">${parseFloat(line.unitCost).toFixed(2)}</td>
                      <td className="p-2">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => startEditLine(line.inventoryId)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => removeLine(line.inventoryId)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {lines.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-4 text-center text-muted-foreground">No hay productos agregados</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-2">
              {lines.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">No hay productos agregados</p>
              ) : (
                lines.map((line, i) => (
                  <div key={line.inventoryId} className="border rounded-lg p-3 space-y-1">
                    <div className="flex justify-between items-start">
                      <span className="font-medium text-sm">{line.productName}</span>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-6 w-6 -mt-1" onClick={() => startEditLine(line.inventoryId)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-6 w-6 -mt-1 -mr-1" onClick={() => removeLine(line.inventoryId)}>
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-1 text-xs text-muted-foreground">
                      <div><span className="font-medium">Stock:</span> {line.currentStock}</div>
                      <div><span className="font-medium">Enviar:</span> {line.quantity}</div>
                      <div><span className="font-medium">Und:</span> ${parseFloat(line.unitCost).toFixed(2)}</div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Add product search */}
            {!showAddProduct && (
              <div className="relative">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={productSearch}
                      onChange={(e) => searchProducts(e.target.value)}
                      onFocus={() => setShowSearch(true)}
                      className="pl-9"
                      placeholder="Buscar producto..."
                    />
                  </div>
                  <Button onClick={() => setShowSearch(!showSearch)} variant="outline" size="sm">
                    {showSearch ? "Cerrar" : "Buscar"}
                  </Button>
                </div>
                {showSearch && productSearch.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 border rounded-lg bg-background shadow-lg max-h-48 overflow-y-auto">
                    {searchResults.length === 0 ? (
                      <div className="p-3 text-sm text-muted-foreground">Sin resultados</div>
                    ) : (
                      searchResults.slice(0, 10).map((item: any) => (
                        <button
                          key={item.id}
                          onClick={() => selectProduct(item)}
                          className="w-full p-3 text-left hover:bg-muted flex justify-between items-center border-b last:border-0"
                        >
                          <span className="font-medium">
                            {item.product?.name || item.productName || "Producto"}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            Stock: {item.stock} | Costo: ${parseFloat(item.averageCost || item.acquisitionCost || "0").toFixed(2)}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Inline edit form */}
            {showAddProduct && editingProduct && (
              <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
                <h4 className="font-medium">{editingProduct.productName}</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <Label>Cantidad a enviar *</Label>
                    <Input
                      type="number" min="1" max={editingProduct.currentStock}
                      value={editingProduct.quantity}
                      onChange={(e) => setEditingProduct((prev) => prev ? { ...prev, quantity: Number(e.target.value) } : null)}
                    />
                    <p className="text-xs text-muted-foreground mt-1">Stock disponible: {editingProduct.currentStock}</p>
                  </div>
                  <div>
                    <Label>Costo unitario (CUP)</Label>
                    <Input
                      value={editingProduct.unitCost}
                      onChange={(e) => setEditingProduct((prev) => prev ? { ...prev, unitCost: e.target.value } : null)}
                    />
                  </div>
                  <div>
                    <Label>Lote</Label>
                    <Input
                      value={editingProduct.batchNumber}
                      onChange={(e) => setEditingProduct((prev) => prev ? { ...prev, batchNumber: e.target.value } : null)}
                      placeholder="Opcional"
                    />
                  </div>
                </div>
                <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => { setShowAddProduct(false); setEditingProduct(null); }}>
                    Cancelar
                  </Button>
                  <Button onClick={confirmAddProduct}>
                    ✓ {lines.some((l) => l.inventoryId === editingProduct.inventoryId) ? "Actualizar" : "Agregar"}
                  </Button>
                </div>
              </div>
            )}

            {/* Summary */}
            <div className="text-sm text-muted-foreground">
              {lines.length} producto(s) | {totalQuantity} unidades
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex flex-col sm:flex-row sm:justify-between gap-2 pt-4 border-t">
          <div className="flex gap-2">
            {step === 2 && (
              <Button variant="outline" size="sm" onClick={() => setStep(1)}>← Volver</Button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">Cancelar</Button>
            {step === 1 ? (
              <Button onClick={() => setStep(2)} disabled={!shopDestinationId} className="w-full sm:w-auto">Continuar →</Button>
            ) : (
              <>
                <Button variant="secondary" onClick={() => handleSave(true)} disabled={isSaving || lines.length === 0} className="w-full sm:w-auto">
                  <Save className="mr-2 h-4 w-4" />
                  {isSaving ? "Guardando..." : "Guardar borrador"}
                </Button>
                <Button onClick={() => handleSave(false)} disabled={isSaving || lines.length === 0} className="w-full sm:w-auto">
                  {isSaving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Enviando...</> : <><Send className="mr-2 h-4 w-4" /> Enviar Transferencia</>}
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
